import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, GraduationCap, HandHeart, Mail, Newspaper, Eye, TrendingUp, Activity } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { api } from '../../lib/api';

const STAT_CARDS = (d) => [
  { label: 'Bağışlar', value: `₺${(d?.donations_total_amount || 0).toLocaleString('tr-TR')}`, sub: `${d?.donations_count || 0} bağış`, icon: Heart, tone: 'coral', to: '/admin/donations' },
  { label: 'Burs Başvurusu', value: d?.scholarships_count || 0, sub: 'tüm başvurular', icon: GraduationCap, tone: 'gold', to: '/admin/scholarships' },
  { label: 'Gönüllüler', value: d?.volunteers_count || 0, sub: 'kayıtlı', icon: HandHeart, tone: 'emerald', to: '/admin/volunteers' },
  { label: 'Mesajlar', value: d?.messages_count || 0, sub: `${d?.messages_unread || 0} okunmamış`, icon: Mail, tone: 'navy', to: '/admin/messages' },
  { label: 'Bugün Ziyaretçi', value: d?.today_views || 0, sub: 'son 24 saat', icon: Eye, tone: 'gold', to: '/admin/analytics' },
  { label: 'Haftalık Görüntülenme', value: d?.week_views || 0, sub: 'son 7 gün', icon: TrendingUp, tone: 'emerald', to: '/admin/analytics' },
  { label: 'Duyurular', value: d?.news_count || 0, sub: 'yayınlanan', icon: Newspaper, tone: 'navy', to: '/admin/news' },
];

const TONE_CLASS = {
  coral: 'bg-coral-50 text-coral-300',
  gold: 'bg-gold/15 text-gold-500',
  emerald: 'bg-emerald-50 text-emerald-600',
  navy: 'bg-ink-50 text-ink-600',
};

export default function AdminDashboardV2() {
  const [data, setData] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    api.adminDashboardV2().then(setData).catch(() => {});
    api.adminAnalyticsSummary(30).then(setAnalytics).catch(() => {});
  }, []);

  const dailyChart = (analytics?.daily || []).map(d => ({
    date: d.date.slice(5),
    views: d.views,
    sessions: d.sessions,
  }));
  const topPages = (analytics?.top_pages || []).slice(0, 6).map(p => ({
    name: p.path.length > 22 ? p.path.slice(0, 20) + '…' : p.path,
    views: p.views,
  }));

  return (
    <div data-testid="admin-dashboard">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Genel Bakış</div>
          <h1 className="font-display text-4xl md:text-5xl mt-2 leading-none">Pano</h1>
          <p className="mt-2 text-ink-400 text-sm">Sitenizdeki son durumu ve faaliyetlerinizi görüntüleyin.</p>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {STAT_CARDS(data).map((c, i) => {
          const Icon = c.icon;
          return (
            <Link key={i} to={c.to} data-testid={`stat-${i}`} className="group relative bg-white rounded-2xl p-5 border border-ink-100 hover:border-gold hover:-translate-y-0.5 hover:shadow-xl transition-all">
              <div className={`w-11 h-11 rounded-xl grid place-items-center ${TONE_CLASS[c.tone]}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="mt-4 text-[10px] tracking-[0.25em] uppercase text-ink-400 font-bold">{c.label}</div>
              <div className="mt-1.5 font-display text-3xl text-ink-600 tabular leading-none truncate">{c.value}</div>
              <div className="mt-2 text-xs text-ink-400">{c.sub}</div>
            </Link>
          );
        })}
      </div>

      {/* Analytics charts */}
      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-ink-100 p-6" data-testid="chart-daily">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Trafik Trendi</div>
              <h2 className="font-display text-xl mt-1">Son 30 gün</h2>
            </div>
            <div className="flex items-center gap-3 text-xs">
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-ink-600"></span> Görüntülenme</span>
              <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-gold"></span> Tekil oturum</span>
            </div>
          </div>
          <div className="h-64">
            {dailyChart.length === 0 ? (
              <div className="h-full grid place-items-center text-ink-400 text-sm">Henüz yeterli veri yok</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={dailyChart} margin={{ top: 5, right: 16, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7dfd1" vertical={false} />
                  <XAxis dataKey="date" stroke="#a0a8b8" fontSize={11} tickLine={false} />
                  <YAxis stroke="#a0a8b8" fontSize={11} tickLine={false} axisLine={false} />
                  <Tooltip contentStyle={{ background: '#0F1E4D', border: 'none', borderRadius: 12, color: '#F4EEE2' }} />
                  <Line type="monotone" dataKey="views" stroke="#0F1E4D" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
                  <Line type="monotone" dataKey="sessions" stroke="#C9A96E" strokeWidth={2.5} dot={false} activeDot={{ r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-ink-100 p-6" data-testid="chart-top-pages">
          <div className="mb-5">
            <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Popülerlik</div>
            <h2 className="font-display text-xl mt-1">En çok ziyaret</h2>
          </div>
          <div className="h-64">
            {topPages.length === 0 ? (
              <div className="h-full grid place-items-center text-ink-400 text-sm">Henüz veri yok</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={topPages} layout="vertical" margin={{ top: 5, right: 16, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e7dfd1" horizontal={false} />
                  <XAxis type="number" stroke="#a0a8b8" fontSize={11} tickLine={false} axisLine={false} />
                  <YAxis type="category" dataKey="name" stroke="#a0a8b8" fontSize={10} tickLine={false} axisLine={false} width={110} />
                  <Tooltip contentStyle={{ background: '#0F1E4D', border: 'none', borderRadius: 12, color: '#F4EEE2' }} />
                  <Bar dataKey="views" fill="#C9A96E" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* 2 column section */}
      <div className="grid lg:grid-cols-3 gap-6 mt-8">
        {/* Recent donations */}
        <div className="lg:col-span-2 bg-white rounded-2xl border border-ink-100 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-5 border-b border-ink-100">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">Son Bağışlar</div>
              <h2 className="font-display text-xl mt-1">En son destekçilerimiz</h2>
            </div>
            <Link to="/admin/donations" className="text-xs text-ink-500 hover:text-ink-600 font-semibold">Tümünü gör →</Link>
          </div>
          <div>
            {(data?.recent_donations || []).slice(0, 6).map((d, i) => (
              <div key={d.id || i} className="flex items-center gap-4 px-6 py-4 border-b border-ink-50 last:border-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-coral-50 to-coral-100 grid place-items-center text-coral-300 font-display font-bold">
                  {(d.info?.name || 'A')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{d.info?.name} {d.info?.surname || ''}</div>
                  <div className="text-xs text-ink-400 truncate">{d.info?.email}</div>
                </div>
                <div className="font-display tabular text-lg">₺{(d.amount || 0).toLocaleString('tr-TR')}</div>
              </div>
            ))}
            {(!data?.recent_donations || data.recent_donations.length === 0) && (
              <div className="px-6 py-8 text-center text-ink-400 text-sm">Henüz bağış yok</div>
            )}
          </div>
        </div>

        {/* Activity */}
        <div className="bg-white rounded-2xl border border-ink-100 overflow-hidden">
          <div className="flex items-center justify-between px-6 py-5 border-b border-ink-100">
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-coral-300 font-bold">İşlem Akışı</div>
              <h2 className="font-display text-xl mt-1">Son aktiviteler</h2>
            </div>
            <Link to="/admin/logs" className="text-xs text-ink-500 hover:text-ink-600 font-semibold">→</Link>
          </div>
          <div className="px-6 py-3">
            {(data?.recent_logs || []).slice(0, 8).map((l, i) => (
              <div key={l.id || i} className="flex items-start gap-3 py-2.5 border-b border-ink-50 last:border-0">
                <div className="w-7 h-7 rounded-lg bg-gold/15 text-gold-500 grid place-items-center shrink-0 mt-0.5">
                  <Activity className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{l.action}</div>
                  <div className="text-[11px] text-ink-400 mt-0.5">
                    {l.username} · {l.created_at ? new Date(l.created_at).toLocaleString('tr-TR') : ''}
                  </div>
                </div>
              </div>
            ))}
            {(!data?.recent_logs || data.recent_logs.length === 0) && (
              <div className="py-8 text-center text-ink-400 text-sm">Henüz işlem yok</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
